import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useOpenBankingAccounts} from './useOpenBankingAccounts';

// Resolves the owning OB account id (for the deep link) and the latest bank name
// for an OB expense's payment method. The stored `ob_payment_method.bank_name` is
// a snapshot taken when the expense was created and can be stale (the account may
// have been renamed since). We walk ob_payment_method.id -> obPaymentMethodOptions
// (account_id) -> accounts (account.name), and fall back to the stored bank_name
// when the option or account isn't loaded/found.
export const resolveObPaymentMethodInfo = ({obPaymentMethod, options = [], accounts = []}) => {
  const fallback = obPaymentMethod?.bank_name;
  if (!obPaymentMethod?.id) return {accountId: undefined, bankName: fallback};

  // A synthetic (not-yet-reviewed) ob_payment_method carries its account_id directly, since
  // its `id` is just the account id and won't be found in obPaymentMethodOptions (those are
  // keyed by the backend's ob_payment_method_id — a different id space).
  const accountId = obPaymentMethod.account_id
    ?? options.find((option) => option.value === obPaymentMethod.id)?.account_id;
  if (!accountId) return {accountId: undefined, bankName: fallback};

  const account = accounts.find((item) => item.id === accountId);
  // Only fall back on a missing account — an intentionally empty name stays empty.
  return {accountId, bankName: account?.name ?? fallback};
};

// Single memoized pass that resolves both the account id and the bank name so a
// consumer (e.g. a per-row cell) doesn't select or scan the options twice.
export const useObPaymentMethodInfo = (obPaymentMethod) => {
  const options = useSelector((state) => state.openBanking?.obPaymentMethodOptions ?? []);
  const accounts = useOpenBankingAccounts();
  return useMemo(
    () => resolveObPaymentMethodInfo({obPaymentMethod, options, accounts}),
    [obPaymentMethod, options, accounts]
  );
};

export const useObBankName = (obPaymentMethod) => useObPaymentMethodInfo(obPaymentMethod).bankName;
